// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — one round of a live race, decoupled from Trystero, the DOM and the clock
// (driven by step()/finish checks), which is what makes the host-transfer contract
// testable without a network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer rolls its OWN marble on a maze that is a pure
// function of the frozen seed, and reaches its OWN game-over (reach the exit, or the
// clock runs out) with no host help. NO ball state ever crosses the wire — only a
// tiny status gossip (gems left as progress, and on finishing: done, won, time,
// falls, so the results can show everyone's run). HOST TRANSFER IS A PURE DISPLAY
// CONCERN; onHostChange is wired regardless.

import { createGame, drainEvents, step as stepGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  gemsLeft: number;
  falls: number;
  finished: boolean;
  won: boolean; // reached the exit (vs DNF on the clock)
  ms: number; // finish time (or the time limit for a DNF)
  left: boolean; // peer dropped out
}

/** Tiny wire status. Progress rides `g`; the finish detail rides the final message. */
export interface RaceMsg {
  g?: number; // gems left (progress)
  fl?: number; // falls so far
  d?: boolean; // done
  w?: boolean; // won (reached the exit)
  ms?: number; // finish time
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a
 *  window; further pokes while pending do not push it back. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        gemsLeft: o.mode.gems,
        falls: 0,
        finished: false,
        won: false,
        ms: 0,
        left: false,
      });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  /** Advance the local marble by `dtMs` under the current tilt. Spectators no-op. */
  step(tilt: { x: number; y: number }, dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    const gemsBefore = this.state.gemsLeft;
    const fallsBefore = this.state.falls;
    stepGame(this.state, tilt, dtMs);
    const events = drainEvents(this.state);

    const me = this.self();
    if (me) {
      me.gemsLeft = this.state.gemsLeft;
      me.falls = this.state.falls;
    }
    if (this.state.gemsLeft !== gemsBefore || this.state.falls !== fallsBefore) {
      this.deps.send({ g: this.state.gemsLeft, fl: this.state.falls });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.finished = true;
      me.won = this.state.finished;
      me.ms = Math.round(this.state.elapsedMs);
      me.gemsLeft = this.state.gemsLeft;
      me.falls = this.state.falls;
    }
    this.deps.send({ d: true, w: this.state.finished, ms: Math.round(this.state.elapsedMs), fl: this.state.falls });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return; // never let the wire overwrite our own run
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.g === 'number') r.gemsLeft = Math.max(0, msg.g | 0);
    if (typeof msg.fl === 'number') r.falls = Math.max(0, msg.fl | 0);
    if (msg.d) {
      r.finished = true;
      r.won = !!msg.w;
      if (typeof msg.ms === 'number') r.ms = Math.max(0, msg.ms | 0);
    }
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.finished) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.finished && !r.left) return false;
    return true;
  }

  /** Fastest winner first; then DNFs by most gems collected; departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      const rank = (r: PeerResult): number => (r.left ? 3 : r.won ? 0 : r.finished ? 1 : 2);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra === 0) return a.ms - b.ms; // both won → faster first
      if (ra === 1) return a.gemsLeft - b.gemsLeft || a.falls - b.falls; // DNF → nearer done first
      return a.seat - b.seat;
    });
  }
}
